/** Public submission protocol. No expected answers or private tests belong here. */
import { BENCH_FORMS } from './efficiency.js';
// pilot-29: cp-scale-and-cost's sweep moved to /tmp, the one place the
// isolated grader lets a check write (docs/missions.md), and the grading image
// gained sqlite3 for cp-database and cp-concurrency.
// pilot-30: the export leaves ignored untracked files behind (they failed the
// clean gate), and cp-craft and cp-legacy run their suite in a writable copy.
// pilot-32: from blind solves (2026-09-26). cp-first-commits asks for each
// change in its own commit; cp-production's postmortem is three objectives so
// a failure says which part is missing; cp-observability's page-rule failure no
// longer gives away how many rules page.
// pilot-33: from a weaker model solving all 25 blind (2026-09-26). Three
// checks that passed work which missed the lesson got hidden behavioural
// checks (cp-pipeline, cp-database) or a coherent scenario (cp-api lists
// newest first); cp-releases and cp-network split their one-sentence-many-
// faults objectives; cp-production's mitigation has to take the fee off, as
// its brief says; and checkpoint failures now carry the matched near-miss
// message, never its script.
// pilot-34: cp-agent-review, the first checkpoint outside the general track
// (docs/ai-track.md), assessing the AI track's first world. Built so that a
// small model working alone fails it: Haiku did, 6 of 8, 2026-09-27.
// pilot-35: every general-track checkpoint a weaker model passed alone in
// round 3 (19 of 25, 2026-09-27) gained judgement traps drawn from its own
// chapter: a claim to check against a diff, a decoy, a fact only the data or
// the history holds, a second consumer or writer, scope to leave alone. Also:
// cp-pipeline rejects product code that drives the test runner, cp-scale-and-
// cost counts calls and callers instead of timing them, and cp-engineer and
// cp-craft split objectives whose one message covered two faults.
// pilot-36: every checkpoint carries Iris (docs/iris.md). Each gained an
// iris: block of planted mistakes, directives the learner never sees that
// make Iris propose a specific wrong step, each proved by a wrong/iris-*.sh
// that fails exactly the objectives it names. A checkpoint is now passed by
// working with an agent and catching what it gets wrong. cp-security's brief
// points to ops/secrets/cardinal.env instead of printing the new key.
// pilot-37: cp-whole-job's timeline-order objective gains two diagnostics,
// so a timeline missing a moment and one with a row out of place stop being
// told "diagnosis first"; what passes and fails is unchanged.
// pilot-38: cp-whole-job's a-test-would-catch-it-next-time rebuilds the bug
// from the seeded main, not the learner's, so a moved main fails only the
// hidden main-untouched instead of also calling a good test suite blind.
//
// What this version is, and what it is not. It is the contract between the
// Worker and the grading service: an attempt is issued, graded and recorded
// under it, and a service running different checks refuses the attempt. It is
// no longer what decides whether a pass *counts*. Until 2026-09-29 every read
// was scoped to it, so each bump — thirty-six in sixteen days, most of them
// fixes to one checkpoint, several of them fixes that stopped a checkpoint
// failing correct work — emptied every learner's badges on all of them at
// once. A pass is now kept at the version that graded it and counts under any
// later one, as certificates always did (docs/certificates.md). The one
// exception is `retired` on a mission below: see `passCounts`.
// pilot-39: timed-out checks are inconclusive, not cached assessment failures.
// pilot-40: every checkpoint has designed agents instead of live Iris
// (docs/designed-agents.md, section E). Its iris: block is gone; an
// iris_script of Iris and Kite carries most of the work, with planted
// mistakes, and one step stays the learner's (iris-finish.sh): approving
// everything fails, the agents' careful path alone fails, and that path
// plus the learner's step passes.
export const VERIFICATION_VERSION = 'pilot-40';
export const VERIFICATION_MISSIONS = {
    // Recovery is assessed by its checkpoint now, not by one of its levels
    // (docs/checkpoints.md, rule 4). `l10-recover-the-lost-commit` was here and
    // is deliberately gone: a world's badge rests on the checkpoint alone,
    // because a claim assembled out of two kinds of evidence is only as strong
    // as the weaker one and a reader cannot tell which half they are looking at.
    'cp-first-commits': { xp: 360 },
    'cp-remotes': { xp: 380 },
    'cp-recovery': { xp: 400 },
    'cp-quality': { xp: 420 },
    // Team Git has one now, so `l19-ship-it` has gone the way l10 and l13 went.
    // It was the only verified *level* left, and keeping it beside a checkpoint
    // would have made this world's badge the one thing rule 4 forbids: a claim
    // assembled out of two kinds of evidence, no stronger than the weaker half
    // and unreadable as to which half you are looking at. `l19-ship-it` is
    // still the only mission `submissionPath` admits `origin.git/` for, which
    // costs nothing now that nothing submits it.
    'cp-team-git': { xp: 440 },
    'cp-code-review': { xp: 460 },
    'cp-the-codebase': { xp: 480 },
    'cp-releases': { xp: 500 },
    'cp-production': { xp: 520 },
    'cp-pipeline': { xp: 540 },
    'cp-data': { xp: 560 },
    'cp-design': { xp: 580 },
    'cp-ownership': { xp: 600 },
    'cp-network': { xp: 620 },
    'cp-database': { xp: 640 },
    'cp-api': { xp: 660 },
    'cp-security': { xp: 680 },
    'cp-concurrency': { xp: 700 },
    'cp-observability': { xp: 720 },
    'cp-scale-and-cost': { xp: 740 },
    'cp-legacy': { xp: 760 },
    'cp-configuration': { xp: 780 },
    'cp-craft': { xp: 800 },
    'cp-engineer': { xp: 820 },
    'cp-whole-job': { xp: 840 },
    // The AI Engineering track's first world. On a badge, never on a
    // certificate: certificates are the general course (certificates.ts).
    'cp-agent-review': { xp: 860 },
};
export const SUBMISSION_MAX_BYTES = 4 * 1024 * 1024;
export const SUBMISSION_MAX_FILES = 2048;
export const SUBMISSION_MAX_FILE_BYTES = 1024 * 1024;
export function pilotMission(id) {
    return Object.hasOwn(VERIFICATION_MISSIONS, id);
}
/**
 * A task of the efficiency benchmark (docs/efficiency-bench.md). Submitted and
 * graded like a checkpoint, and never a pilot level: no XP, no badge, no
 * verified completion. Only the study's own route opens an attempt for one.
 */
export function benchMission(id) {
    return Object.values(BENCH_FORMS).some((form) => form.includes(id));
}
/**
 * Whether a pass of `missionId` recorded under grading `version` still counts.
 *
 * Every read of `verified_completions` that decides something — a badge, the
 * project board's gate, a learner's own list — asks this, or the SQL the
 * Worker builds from `retiredPasses`, which says the same thing. A mission
 * this deployment no longer verifies counts for nothing: the page has no name
 * to draw it under.
 */
export function passCounts(missionId, version) {
    if (!pilotMission(missionId))
        return false;
    return !VERIFICATION_MISSIONS[missionId].retired?.includes(version);
}
/** Every (mission, version) pair `passCounts` refuses for a verified mission, for the Worker's SQL. */
export function retiredPasses() {
    return Object.entries(VERIFICATION_MISSIONS)
        .flatMap(([missionId, m]) => (m.retired ?? []).map((version) => ({ missionId, version })));
}
/** Git configuration, hooks, replacement refs, alternates and reflogs never travel. */
export function submissionPath(path, missionId) {
    if (path.length > 240 || /[\\\x00-\x1f\x7f]/.test(path))
        return false;
    const segments = path.split('/');
    if (segments.some((p) => !p || p === '.' || p === '..'))
        return false;
    let git = null;
    if (path.startsWith('storefront/.git/'))
        git = path.slice(16);
    else if (path.startsWith('origin.git/') && missionId === 'l19-ship-it') {
        git = path.slice(11);
        if (/^pulls\/[1-9][0-9]{0,3}\.json$/.test(git))
            return true;
    }
    else {
        return segments[0] === 'storefront' && segments.length > 1 &&
            !segments.some((p) => p.toLowerCase() === '.git' || p === 'node_modules');
    }
    return /^(HEAD|index|packed-refs)$/.test(git) ||
        /^objects\/[0-9a-f]{2}\/[0-9a-f]{38}$/.test(git) ||
        /^objects\/pack\/pack-[0-9a-f]{40}\.(pack|idx)$/.test(git) ||
        /^refs\/(heads|tags|remotes)\/[A-Za-z0-9_./-]+$/.test(git);
}
export function validateSubmission(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return 'Expected a submission';
    const s = value;
    if (s.format !== 1 || typeof s.missionId !== 'string' || !(pilotMission(s.missionId) || benchMission(s.missionId)))
        return 'Unsupported mission or format';
    if (Object.keys(s).some((k) => !['format', 'missionId', 'files'].includes(k)))
        return 'Unexpected submission field';
    if (!Array.isArray(s.files) || !s.files.length || s.files.length > SUBMISSION_MAX_FILES)
        return 'Too many or missing files';
    const names = new Set();
    let bytes = 0;
    for (const f of s.files) {
        if (!f || typeof f !== 'object' || Object.keys(f).some((k) => !['path', 'data', 'executable'].includes(k)) ||
            typeof f.path !== 'string' || !submissionPath(f.path, s.missionId) ||
            typeof f.executable !== 'boolean' || typeof f.data !== 'string')
            return 'Unsupported file';
        if (names.has(f.path))
            return 'Duplicate file';
        names.add(f.path);
        const decodedBytes = f.data.length / 4 * 3 - (f.data.endsWith('==') ? 2 : f.data.endsWith('=') ? 1 : 0);
        if (decodedBytes > SUBMISSION_MAX_FILE_BYTES || f.data.length % 4 !== 0 ||
            !/^[A-Za-z0-9+/]*={0,2}$/.test(f.data))
            return 'Invalid or oversized file';
        bytes += f.data.length + f.path.length;
        if (bytes > SUBMISSION_MAX_BYTES)
            return 'Submission is too large';
    }
    for (const name of names) {
        const parts = name.split('/');
        parts.pop();
        while (parts.length) {
            if (names.has(parts.join('/')))
                return 'A file is also a directory';
            parts.pop();
        }
    }
    if (!names.has('storefront/.git/HEAD') || !names.has('storefront/.git/index'))
        return 'Repository or index is missing';
    return null;
}
/**
 * The checkpoints, and the worlds they speak for.
 *
 * Hard-coded rather than derived from the mission catalogue, because both
 * readers of this table — the Worker, which has no mission list by design, and
 * the public page, which refuses to download 230 KB of level specifications to
 * draw three badges — have no catalogue to derive it from. What that costs is
 * a table that can drift from the levels it describes, so it is not allowed
 * to: `server/test/verification.badges.test.ts` reads `missions/` off the disk
 * and fails if a world here is spelled differently there, if `worldLevels`
 * disagrees with how many levels that world actually has, or if a pilot
 * mission is missing from every badge.
 */
const BADGE_TABLE = [
    { world: 'First Commits', missions: ['cp-first-commits'], worldLevels: 2 },
    { world: 'Branches & Remotes', missions: ['cp-remotes'], worldLevels: 1 },
    { world: 'Recovery', missions: ['cp-recovery'], worldLevels: 1 },
    { world: 'Quality', missions: ['cp-quality'], worldLevels: 1 },
    { world: 'Team Git', missions: ['cp-team-git'], worldLevels: 1 },
    { world: 'Code Review', missions: ['cp-code-review'], worldLevels: 1 },
    { world: 'The Codebase', missions: ['cp-the-codebase'], worldLevels: 1 },
    { world: 'Releases', missions: ['cp-releases'], worldLevels: 1 },
    { world: 'Production', missions: ['cp-production'], worldLevels: 1 },
    { world: 'The Pipeline', missions: ['cp-pipeline'], worldLevels: 1 },
    { world: 'Data', missions: ['cp-data'], worldLevels: 1 },
    { world: 'Design', missions: ['cp-design'], worldLevels: 1 },
    { world: 'Ownership', missions: ['cp-ownership'], worldLevels: 1 },
    { world: 'The Network', missions: ['cp-network'], worldLevels: 1 },
    { world: 'The Database', missions: ['cp-database'], worldLevels: 1 },
    { world: 'The API', missions: ['cp-api'], worldLevels: 1 },
    { world: 'Security', missions: ['cp-security'], worldLevels: 1 },
    { world: 'Concurrency', missions: ['cp-concurrency'], worldLevels: 1 },
    { world: 'Observability', missions: ['cp-observability'], worldLevels: 1 },
    { world: 'Scale & Cost', missions: ['cp-scale-and-cost'], worldLevels: 1 },
    { world: 'Legacy', missions: ['cp-legacy'], worldLevels: 1 },
    { world: 'Configuration', missions: ['cp-configuration'], worldLevels: 1 },
    { world: 'The Craft', missions: ['cp-craft'], worldLevels: 1 },
    { world: 'The Engineer', missions: ['cp-engineer'], worldLevels: 1 },
    { world: 'The Whole Job', missions: ['cp-whole-job'], worldLevels: 1 },
    { world: "The agent's pull request", missions: ['cp-agent-review'], worldLevels: 1 },
];
/**
 * Levels whose starting repository is derived per learner (packages/engine/src/salt.ts).
 *
 * The one place that decides. Both halves of salting read it — the Worker,
 * which will not hand out a salt for a level that is not listed, and the
 * grader, which will not apply one — so a level cannot be salted on one side
 * and plain on the other, which would fail every submission for it.
 *
 * A level joins this list only once its own checks have been re-validated
 * against a salted baseline, because the salt puts a commit on `main` that the
 * level's objectives were not written against. That is per-level work and it
 * is why this is a list rather than a boolean.
 */
export const SALTED_MISSIONS = [
    'cp-first-commits',
    'cp-remotes',
    'cp-recovery',
    'cp-quality',
    'cp-team-git',
    'cp-code-review',
    'cp-the-codebase',
    'cp-releases',
    'cp-production',
    'cp-pipeline',
    'cp-data',
    'cp-design',
    'cp-ownership',
    'cp-network',
    'cp-database',
    'cp-api',
    'cp-security',
    'cp-concurrency',
    'cp-observability',
    'cp-scale-and-cost',
    'cp-legacy',
    'cp-configuration',
    'cp-craft',
    'cp-engineer',
    'cp-whole-job',
    'cp-agent-review',
];
/** Whether this level's workspace is the learner's own rather than everybody's. */
export function saltedMission(missionId) {
    // The efficiency benchmark too (docs/efficiency-bench.md): every attempt
    // the study funds is the participant's own repository. Not in the list
    // above, which is the badges' (a salted level there is a verified level).
    return SALTED_MISSIONS.includes(missionId) || benchMission(missionId);
}
/** Every mission a salt is handed out for: the salted checkpoints and the benchmark's tasks. */
export function saltedMissionIds() {
    return [...SALTED_MISSIONS, ...Object.values(BENCH_FORMS).flat()];
}
/**
 * The badges, with `salted` computed rather than written down.
 *
 * It was a literal in the table and that was a mistake waiting to happen: the
 * flag decides which sentence a stranger reads on somebody's public page, and
 * a hand-maintained copy of a fact kept somewhere else is how a page ends up
 * claiming a learner was verified individually when the level is still seeded
 * from the same bytes for everybody. Now the claim cannot outrun the
 * implementation, because it is read from it.
 *
 * Every checkpoint in a world must be salted before the badge says so. A world
 * confirmed partly by salted levels and partly by shared ones is exactly as
 * strong as its weakest level, and the badge says the weaker thing.
 */
export const VERIFICATION_BADGES = BADGE_TABLE.map((badge) => ({
    ...badge,
    salted: badge.missions.every(saltedMission),
}));
/** The badge a checkpoint level belongs to, or undefined for every other level. */
export function badgeForMission(missionId) {
    return VERIFICATION_BADGES.find((b) => b.missions.includes(missionId));
}
/**
 * The badges a set of confirmed missions earns, in table order.
 *
 * Every mission of a badge must be present. The caller is expected to have
 * filtered with `passCounts` already. The missions may have been passed under
 * different grading versions; each pass is a real pass of the checks that
 * graded it, and a later version of one checkpoint says nothing about another.
 */
export function badgesEarned(missionIds) {
    const held = missionIds instanceof Set ? missionIds : new Set(missionIds);
    return VERIFICATION_BADGES.filter((b) => b.missions.every((id) => held.has(id)));
}
//# sourceMappingURL=verification.js.map